import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { faPenToSquare } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Textarea } from '@repo/videa-components/src/ui/textarea';

import { NotePMSStatusAlert } from './NotePMSStatusAlert';

export interface NoteEditorProps {
  noteContent: string;
  isNoteReadOnly: boolean | '' | null | undefined;
  isNoteSavedToPMS: boolean | null | undefined;
  isNoteBeingSavedToPMS: boolean | '' | null | undefined;
  isSavingNoteUpdate?: boolean;
  onNoteChange: (value: string) => void;
  onNoteClick?: () => void;
  onFeedbackSubmitted?: (rating: 'up' | 'down') => void; // Callback after successful feedback
  noteId?: string;
  feedbackTraceId?: string; // Langfuse trace ID from backend
  existingRating?: 'up' | 'down'; // Existing rating from backend
  standalonePatientVisitId?: string;
  // Optional feedback widget component (from consuming app)
  feedbackWidget?: ReactNode;
  // Optional translation function (accepts any to be compatible with lingui's t function)
  t?: (key: string | any) => string;
}

export const NoteEditor = ({
  noteContent,
  isNoteReadOnly,
  isNoteSavedToPMS,
  isNoteBeingSavedToPMS,
  isSavingNoteUpdate = false,
  onNoteChange,
  onNoteClick,
  onFeedbackSubmitted,
  noteId,
  feedbackTraceId,
  standalonePatientVisitId,
  feedbackWidget,
  t = (key: string) => key,
}: NoteEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    // Auto-focus and place cursor in the top 1/3 only on first render
    if (textareaRef.current && !isNoteReadOnly && !hasAutoFocusedRef.current) {
      // Use setTimeout to ensure the textarea is fully rendered
      const timeoutId = setTimeout(() => {
        if (textareaRef.current && !hasAutoFocusedRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(0, 0);
          // Scroll to the top to show the beginning of the text
          textareaRef.current.scrollTop = 0;
          hasAutoFocusedRef.current = true;
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isNoteReadOnly]);

  return (
    <div className="vai-bg-primary-foreground vai-border vai-border-gray-200 vai-rounded-md vai-flex vai-flex-col vai-flex-1 vai-min-h-[600px]">
      {/* Header */}
      <div className="vai-flex vai-items-center vai-justify-between vai-px-4 vai-py-2 vai-flex-shrink-0">
        <div className="vai-flex vai-items-center vai-gap-2">
          {isNoteReadOnly ? (
            <NotePMSStatusAlert
              isNoteReadOnly={true}
              isNoteSavedToPMS={isNoteSavedToPMS}
              isNoteBeingSavedToPMS={isNoteBeingSavedToPMS}
            />
          ) : (
            <>
              <FontAwesomeIcon
                icon={faPenToSquare}
                className="vai-text-black"
              />
              <p className="vai-text-[14px] vai-text-black">
                <span className="vai-font-semibold">
                  {t('Edit your note in Videa — help our AI learn your style.')}
                </span>
              </p>
            </>
          )}
        </div>
        <div className="vai-flex vai-items-center vai-gap-2">
          {!isNoteBeingSavedToPMS && !isNoteSavedToPMS && (
            <>
              {isSavingNoteUpdate ? (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="vai-text-green-600 vai-font-weight-900 vai-line-height-normal"
                  />
                  <p className="vai-font-inter vai-text-xs vai-text-black vai-font-medium vai-leading-none">
                    {t('Saving...')}
                  </p>
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="vai-text-green-600 vai-font-weight-900 vai-line-height-normal"
                  />
                  <p className="vai-font-inter vai-text-xs vai-text-green-600 vai-font-medium vai-leading-none">
                    {t('Changes Saved')}
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="vai-border-t vai-border-gray-200 vai-flex-shrink-0" />

      {/* Content */}
      <div className="vai-flex vai-flex-col vai-space-y-4 vai-px-4 vai-pt-4 vai-pb-4 vai-flex-1 vai-min-h-0 vai-overflow-hidden">
        <Textarea
          ref={textareaRef}
          id="clinical-note"
          name="clinical-note"
          value={noteContent}
          autoResize={false}
          disabled={!!isNoteReadOnly}
          onClick={onNoteClick}
          onChange={(e) => onNoteChange(e.target.value)}
          className={`vai-min-h-[500px] vai-flex-1 vai-overflow-y-auto vai-resize-none vai-p-4 vai-border-gray-300 ${
            isNoteReadOnly ? 'vai-bg-gray-100 vai-cursor-not-allowed' : ''
          }`}
        />
      </div>

      {/* Feedback Widget Section - Only show if feedbackTraceId exists and feedbackWidget is provided */}
      {noteId && feedbackTraceId && feedbackWidget && (
        <>
          <div className="vai-border-t vai-border-gray-200 vai-flex-shrink-0" />
          <div className="vai-flex vai-justify-center vai-px-4 vai-py-3">
            {feedbackWidget}
          </div>
        </>
      )}
    </div>
  );
};
